import {useState} from 'react';
import {Card} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {NatRule} from '../../Types/routerForms';
import {useAddNatRule, useMoveNatRule, useRemoveNatRule} from '../../Hooks/RouterManager';
import AddNatRuleModal from './AddNatRuleModal';
import ActionIcon from '../../../../Components/ActionIcon';
import Level3Title from '../../../../app/common/components/title/Level3Title';

interface NatRulesSectionProps {
  routerId: string;
  rules: NatRule[];
  publicIps: string[];
  privateNetworks: string[];
}

const NatRulesSection = ({
  routerId,
  rules,
  publicIps,
  privateNetworks,
}: NatRulesSectionProps): JSX.Element => {
  const intl = useIntl();
  const [showAdd, setShowAdd] = useState(false);
  const addRule = useAddNatRule(routerId);
  const removeRule = useRemoveNatRule(routerId);
  const moveRule = useMoveNatRule(routerId);

  const moveUp = (id: string) => moveRule.mutate(id);

  const handleDelete = (id: string) => {
    if (window.confirm(intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.CONFIRM_DELETE'}))) {
      removeRule.mutate(id);
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <Card.Title>
          <Level3Title
            title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVE.NAT_RULES'})}
          />
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <table className="table table-row-dashed align-middle">
          <thead>
            <tr className="fw-semibold text-muted">
              <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PROTOCOL'})}</th>
              <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ORIGIN_IP'})}</th>
              <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC_IP'})}</th>
              <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PUBLIC_PORT'})}</th>
              <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVATE_IP'})}</th>
              <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.PRIVATE_PORT'})}</th>
              <th style={{width: 90}}></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={7} className="text-muted text-center">
                  {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.EMPTY'})}
                </td>
              </tr>
            )}
            {rules.map((r, idx) => (
              <tr key={r.id}>
                <td>{r.protocol.toUpperCase()}</td>
                <td>{r.originIp || <span className="text-muted">—</span>}</td>
                <td>{r.publicIp}</td>
                <td>{r.publicPort}</td>
                <td>{r.privateIp}</td>
                <td>{r.privatePort}</td>
                <td>
                  {idx > 0 && (
                    <ActionIcon
                      label={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.MOVE_UP'})}
                      faIcon="fa-solid fa-arrow-up text-primary"
                      onClick={() => moveUp(r.id)}
                    />
                  )}
                  <ActionIcon
                    label={intl.formatMessage({id: 'COMMON.DELETE'})}
                    faIcon="fa-solid fa-trash text-danger"
                    onClick={() => handleDelete(r.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-light btn-sm" onClick={() => setShowAdd(true)}>
          <i className="fa-solid fa-plus me-2" />
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.FIREWALL.ADD_RULE'})}
        </button>
        <AddNatRuleModal
          show={showAdd}
          onHide={() => setShowAdd(false)}
          onAdd={(r) =>
            addRule.mutate({
              protocol: r.protocol,
              originIp: r.originIp,
              publicIp: r.publicIp,
              publicPort: r.publicPort,
              privateIp: r.privateIp,
              privatePort: r.privatePort,
            })
          }
          publicIps={publicIps}
          privateNetworks={privateNetworks}
        />
      </Card.Body>
    </Card>
  );
};

export default NatRulesSection;
